import React, { useEffect, useState } from 'react';
import { useAppStore } from '@/store';
import { TelegramUtils } from '@/utils/telegram';
import { api } from '@/api';
import { cn } from '@/utils';
import logoLight from '@/assets/itinder.svg';
import logoDark from '@/assets/itinder_dark.svg';
import { Button } from '@/components/ui';

type LookingForType = 'male' | 'female' | 'all';

const LookingForScreen: React.FC = () => {
  const { setActiveScreen, setOnboardingCompleted, setUser, user, onboarding } = useAppStore();
  const [selected, setSelected] = useState<LookingForType | null>(null);
  const [selectedGoals, setSelectedGoals] = useState<number[]>([]);
  const [meetingGoals, setMeetingGoals] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingGoals, setLoadingGoals] = useState(true);

  useEffect(() => {
    console.log('📊 LookingForScreen: Current onboarding data:', onboarding.data);
  }, []);

  useEffect(() => {
    const loadGoals = async () => {
      try {
        const goals = await (api as any).getMeetingGoals();
        setMeetingGoals(goals);
      } catch (error) {
        console.error('Failed to load meeting goals:', error);
      } finally {
        setLoadingGoals(false);
      }
    };
    loadGoals();
  }, []);

  useEffect(() => {
    TelegramUtils.configureBackButton(false);
    TelegramUtils.configureMainButton({ visible: false });
  }, []);

  const handleSelect = (choice: LookingForType) => {
    setSelected(choice);
    TelegramUtils.haptic.selection();
  };

  const handleContinue = async () => {
    if (!selected) return;

    TelegramUtils.haptic.light();
    setLoading(true);

    try {
      // Update looking_for and meeting_goal_ids via API
      await (api as any).updateBasicInfo({
        looking_for: selected,
        meeting_goal_ids: selectedGoals,
      });
      
      console.log('✅ Looking for updated via API:', selected);
      console.log('✅ Meeting goals updated via API:', selectedGoals);
      
      // Get updated profile
      const profile = await api.getMyProfile();
      setUser(profile);
      
      console.log('📊 Profile after looking_for update:', {
        gender: profile.gender,
        is_it: profile.is_it,
        looking_for: profile.looking_for,
        status: profile.status,
      });
      
      // Route based on is_it status
      if (profile.is_it === true) {
        // IT user (male or female) - go to IT Quest test
        console.log('→ IT user, going to IT Quest');
        setActiveScreen('it-quest');
      } else {
        // Non-IT female user - complete registration
        console.log('→ Non-IT user, registration complete');
        setOnboardingCompleted(true);
        setActiveScreen('feed');
      }
      
      TelegramUtils.haptic.success();
    } catch (error) {
      console.error('Failed to set looking_for:', error);
      await TelegramUtils.showAlert('Ошибка при сохранении предпочтений. Проверьте подключение к серверу.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-tg-bg">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-400">Сохраняем предпочтения...</p>
        </div>
      </div>
    );
  }

  const logo = TelegramUtils.isLightTheme ? logoDark : logoLight;

  return (
    <div className="flex flex-col items-center min-h-screen bg-tg-bg p-6 pt-12 pb-8 safe-area-y overflow-y-auto">
      {/* Logo */}
      <div className="mb-8">
        <img 
          src={logo} 
          alt="ITinder" 
          className="w-24 h-auto mb-4"
        />
      </div>

      {/* Main Question */}
      <div className="text-center mb-12">
        <h2 className="text-2xl font-bold text-white mb-3 leading-tight">
          Кого вы ищете?
        </h2>
        <p className="text-lg text-neutral-400">
          Выберите ваши предпочтения
        </p>
      </div>

      {/* Looking For Options */}
      <div className="w-full max-w-sm space-y-3 mb-8">
        {/* Looking for Males */}
        <button
          onClick={() => handleSelect('male')}
          className="w-full text-left"
        >
          <div className={`p-4 rounded-xl border-2 transition-all ${
            selected === 'male'
              ? 'border-white bg-white/10 text-white light:border-neutral-800 light:bg-neutral-200 light:text-neutral-900'
              : 'border-white/20 bg-white/5 text-neutral-400 hover:border-white/40 light:border-neutral-300 light:bg-neutral-100 light:text-neutral-700 light:hover:border-neutral-400'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selected === 'male' 
                  ? 'border-white bg-white light:border-neutral-800 light:bg-neutral-800' 
                  : 'border-white/40 light:border-neutral-400'
              }`}>
                {selected === 'male' && (
                  <div className="w-2.5 h-2.5 bg-black rounded-full light:bg-white" />
                )}
              </div>
              <div className="flex-1">
                <div className="font-semibold">
                  👨 Парня
                </div>
              </div>
            </div>
          </div>
        </button>

        {/* Looking for Females */}
        <button
          onClick={() => handleSelect('female')}
          className="w-full text-left"
        >
          <div className={`p-4 rounded-xl border-2 transition-all ${
            selected === 'female'
              ? 'border-white bg-white/10 text-white light:border-neutral-800 light:bg-neutral-200 light:text-neutral-900'
              : 'border-white/20 bg-white/5 text-neutral-400 hover:border-white/40 light:border-neutral-300 light:bg-neutral-100 light:text-neutral-700 light:hover:border-neutral-400'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selected === 'female' 
                  ? 'border-white bg-white light:border-neutral-800 light:bg-neutral-800' 
                  : 'border-white/40 light:border-neutral-400'
              }`}>
                {selected === 'female' && (
                  <div className="w-2.5 h-2.5 bg-black rounded-full light:bg-white" />
                )}
              </div>
              <div className="flex-1">
                <div className="font-semibold">
                  👩 Девушку
                </div>
              </div>
            </div>
          </div>
        </button>

        {/* Looking for All */}
        <button
          onClick={() => handleSelect('all')}
          className="w-full text-left"
        >
          <div className={`p-4 rounded-xl border-2 transition-all ${
            selected === 'all'
              ? 'border-white bg-white/10 text-white light:border-neutral-800 light:bg-neutral-200 light:text-neutral-900'
              : 'border-white/20 bg-white/5 text-neutral-400 hover:border-white/40 light:border-neutral-300 light:bg-neutral-100 light:text-neutral-700 light:hover:border-neutral-400'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                selected === 'all' 
                  ? 'border-white bg-white light:border-neutral-800 light:bg-neutral-800' 
                  : 'border-white/40 light:border-neutral-400'
              }`}>
                {selected === 'all' && (
                  <div className="w-2.5 h-2.5 bg-black rounded-full light:bg-white" />
                )}
              </div>
              <div className="flex-1">
                <div className="font-semibold">
                  👥 Не важно
                </div>
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Meeting Goals Section */}
      {!loadingGoals && meetingGoals.length > 0 && (
        <div className="w-full max-w-sm mb-8">
          <div className="text-center mb-4">
            <h3 className="text-lg font-bold text-white mb-2">
              Цели знакомства
            </h3>
            <p className="text-sm text-neutral-400">
              Выберите одну или несколько целей
            </p>
          </div>
          
          <div className="flex flex-wrap gap-2 justify-center">
            {meetingGoals.map((goal) => (
              <button
                key={goal.id}
                onClick={() => {
                  setSelectedGoals(prev => 
                    prev.includes(goal.id)
                      ? prev.filter(id => id !== goal.id)
                      : [...prev, goal.id]
                  );
                  TelegramUtils.haptic.selection();
                }}
                className={cn(
                  'px-4 py-2 rounded-full text-sm font-medium transition-all',
                  selectedGoals.includes(goal.id)
                    ? 'bg-white text-black'
                    : 'bg-white/10 text-white/70 border border-white/20 hover:bg-white/20'
                )}
              >
                {goal.icon} {goal.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Info hint */}
      <div className="w-full max-w-sm mb-8">
        <div className="bg-neutral-900/50 border border-neutral-800 rounded-xl p-4">
          <p className="text-xs text-neutral-500">
            ℹ️ Вы будете видеть только тех, кто тоже ищет ваш пол
          </p>
        </div>
      </div>

      {/* Continue Button */}
      <div className="w-full max-w-sm">
        <Button
          onClick={handleContinue}
          className="w-full h-14 bg-white text-black hover:bg-neutral-100 font-bold text-base rounded-2xl transition-all active:scale-95 disabled:opacity-50"
          disabled={!selected || loading}
          loading={loading}
        >
          Продолжить
        </Button>
      </div>
    </div>
  );
};

export default LookingForScreen;

